/**
 * Cálculo de importes de un trabajo (visita).
 *
 * Honorarios: kg de novillo × precio del novillo.
 * Movilidad: independiente del honorario (Spec §15).
 *
 * ## Precio pendiente
 *
 * Si falta el precio de referencia, el importe correspondiente es `null`
 * (PENDIENTE), nunca 0. La ausencia de precio no puede representarse como un
 * importe monetario válido: `null` se propaga al total y bloquea la liquidación.
 *
 * ## Fórmula de movilidad
 *
 * Compatible con el sistema legacy y con Spec §15:
 *
 *     importe = km × tarifa_por_km
 *
 * En `per_km_fuel` la tarifa por km **es** el precio de referencia del
 * combustible configurado por la organización. No se divide por rendimiento ni
 * por litros: el precio configurado se usa directamente como importe por
 * kilómetro, igual que `KM × PRECIO NAFTA` en AppSheet.
 */
import { assertCents, kgToCents, roundKg, type Cents, type Kg } from '@/core/money/cents';
import {
  EconomicsError,
  type MobilityInput,
  type WorkAmounts,
  type WorkItemInput,
  type WorkSessionInput,
} from './types';

/** Subtotal en kg de una línea. `per_job` ignora la cantidad (Spec §11). */
export function computeItemKg(item: WorkItemInput): Kg {
  if (item.kgPerUnit < 0) {
    throw new EconomicsError(`kgPerUnit negativo en "${item.serviceName}"`);
  }
  if (item.quantity < 0) {
    throw new EconomicsError(`Cantidad negativa en "${item.serviceName}"`);
  }
  return item.calculation === 'per_job'
    ? roundKg(item.kgPerUnit)
    : roundKg(item.quantity * item.kgPerUnit);
}

export interface MobilityAmount {
  /** `null` = pendiente por falta de precio de referencia. */
  amountCents: Cents | null;
  pricePending: boolean;
}

export function computeMobilityAmount(mobility: MobilityInput | null | undefined): MobilityAmount {
  // Caso F: "no cobrar movilidad" es un cero real y definitivo, no un pendiente.
  if (!mobility || mobility.mode === 'none') {
    return { amountCents: 0, pricePending: false };
  }

  switch (mobility.mode) {
    case 'per_km_fuel':
    case 'per_km_fixed': {
      const km = mobility.km ?? 0;
      if (km < 0) throw new EconomicsError(`Kilómetros negativos: ${km}`);

      // Sin kilómetros no hay nada que valorizar: cero real, aunque falte la tarifa.
      if (km === 0) return { amountCents: 0, pricePending: false };

      const rate = mobility.ratePerKmCents;
      if (rate === null || rate === undefined) {
        return { amountCents: null, pricePending: true };
      }
      assertCents(rate, 'tarifa por km');
      return { amountCents: kgToCents(km, rate), pricePending: false };
    }
    case 'flat':
    case 'manual': {
      const amount = mobility.amountCents ?? 0;
      assertCents(amount, 'importe de movilidad');
      if (amount < 0) throw new EconomicsError(`Importe de movilidad negativo: ${amount}`);
      return { amountCents: amount, pricePending: false };
    }
    default: {
      const exhaustive: never = mobility.mode;
      throw new EconomicsError(`Modo de movilidad desconocido: ${String(exhaustive)}`);
    }
  }
}

export function computeWorkAmounts(session: WorkSessionInput): WorkAmounts {
  const totalKg = roundKg(session.items.reduce((acc, item) => acc + computeItemKg(item), 0));

  const steerPrice = session.steerPricePerKgCents;
  let honorariaCents: Cents | null;
  let honorariaPending = false;

  if (steerPrice !== null) {
    honorariaCents = kgToCents(totalKg, steerPrice);
  } else if (totalKg > 0) {
    // Hay kg generados pero no se pueden valorizar: PENDIENTE, no $0 (Spec §14).
    honorariaCents = null;
    honorariaPending = true;
  } else {
    // Sin kg que valorizar, el honorario es cero real.
    honorariaCents = 0;
  }

  const mobility = computeMobilityAmount(session.mobility);
  const pricePending = honorariaPending || mobility.pricePending;

  // Un total con alguna parte pendiente es, en sí mismo, pendiente.
  const totalCents =
    honorariaCents === null || mobility.amountCents === null
      ? null
      : honorariaCents + mobility.amountCents;

  return {
    totalKg,
    honorariaCents,
    mobilityCents: mobility.amountCents,
    totalCents,
    pricePending,
  };
}

/**
 * Devuelve el importe si está determinado, o lanza error si está pendiente.
 * Úsese en todo punto donde un importe deba tratarse como dinero real:
 * liquidación, cierre, exportación y agregación.
 */
export function requireAmount(amount: Cents | null, label = 'importe'): Cents {
  if (amount === null) {
    throw new EconomicsError(`El ${label} está pendiente y no puede usarse como importe monetario`);
  }
  return amount;
}
